import { Inject, Injectable } from '@angular/core';
import * as Rx from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { BILLING_CONFIG } from './billing.config';
import { BillingConfig } from './billing.interface';

type Validation = boolean | undefined;

type UrlGetter = () => Promise<string>;

interface Style {
  [key: string]: Validation;
}

export enum CCFormFields {
  NUMBER = 'number',
  CCV = 'ccv',
  EXPIRY = 'expiry',
  NAME = 'name',
  ISSUING_CURRENCY_CODE = 'fx-issuing-currency', // For Native Charge
  POST_CODE = 'postcode', // For AVS for US users
  COUNTRY_CODE = 'ebanxCountryCode', // For Latin users
  EMAIL = 'ebanxEmail', // For Latin users
  PAYMENT_CURRENCY_CODE = 'ebanxCurrencyCode', // For Latin users - same as ISSUING_CURRENCY_CODE
  PHONE = 'ebanxPhone', // For Latin users
  CITY = 'ebanxCity', // For Latin users
  ADDRESS = 'ebanxAddress', // For Latin users
  STREET_COMPLEMENT = 'ebanxStreetComplement', // For Latin users
  STREET_NUMBER = 'ebanxStreetNo', // For Latin users
  ZIP = 'ebanxZip', // For Latin users
  STATE = 'ebanxState', // For Latin users
  DOCUMENT = 'ebanxDocument', // For Latin users
  BIRTH_DATE = 'ebanxBirthdate', // For Latin users
  RUT = 'chileanDocument',

  // For 3DS version 2
  BROWSER_INFO_SCREEN_WIDTH = 'browserScreenWidth',
  BROWSER_INFO_SCREEN_HEIGHT = 'browserScreenHeight',
  BROWSER_INFO_COLOUR_DEPTH = 'browserColourDepth',
  BROWSER_INFO_USER_AGENT = 'browserUserAgent',
  BROWSER_INFO_TZONE_OFFSET = 'browserTZoneOffset',
  BROWSER_INFO_LANGUAGE = 'browserLanguage',
  BROWSER_INFO_JAVA_ENABLED = 'browserJavaEnabled',
  BROWSER_INFO_JSCRIPT_ENABLED = 'browserJScriptEnabled',
}

export type CCFormExtraPayload = { [key in CCFormFields]?: string };

export interface Fields {
  [key: string]: HTMLIFrameElement;
  pan: HTMLIFrameElement;
  cvv: HTMLIFrameElement;
  name: HTMLIFrameElement;
  expiry: HTMLIFrameElement;
}

const slaveFields: Readonly<(keyof Fields)[]> = ['cvv', 'name', 'expiry'];

@Injectable({
  providedIn: 'root',
})
export class Billing {
  private origin: string;
  private url: UrlGetter;
  private fields: Fields;
  // FIXME: enable that?
  // private callback: (e: MessageEvent) => void;

  constructor(@Inject(BILLING_CONFIG) private billingConfig: BillingConfig) {
    this.origin = this.billingConfig.baseUrl;
  }

  initialise(url: UrlGetter, fields: Fields) {
    this.url = url;
    this.fields = fields;
    fields.pan.addEventListener('load', () => {
      if (fields && fields.pan && fields.pan.contentWindow) {
        try {
          if (
            fields.pan.contentWindow.location.origin &&
            fields.pan.contentWindow.location.origin !== this.origin
          ) {
            return;
          }
        } catch (err) {
          /**
            What's with the try catch blocks?

            The problem is that the `load` is emitted twice. Once when the `<iframe>`
              tag is placed on DOM, but the iframe doesn't have the `src` attr yet, and
              2nd time when the iframe loads the actual content we want.

            The solution is a bit hacky, but what we're essentially catching
              in the try/catch block is the attempt to read cross origin
              frame properties. If it's thrown it means that the correct
              frame is loaded, because the browser blocks property access
              which means we can call the `.postMessage` on the iframe window.

            Additionally, this behaviour tends to be a bit race-condition prone so
              there's no guarantee that the empty iframe `load` event will be emited first.
           */
        }
        fields.pan.contentWindow.postMessage('pan', this.origin);
        slaveFields.forEach(slaveField => {
          const field = fields[slaveField];
          if (field && field.contentWindow) {
            try {
              if (
                field.contentWindow.location.origin &&
                field.contentWindow.location.origin !== this.origin
              ) {
                return;
              }
            } catch (err) {
              // see comment above
              field.contentWindow.postMessage('clear', this.origin);
            }
          }
        });
      }
    });
    slaveFields.forEach(slaveField => {
      const field = fields[slaveField];
      if (field) {
        field.addEventListener('load', () => {
          if (field && field.contentWindow) {
            try {
              if (
                field.contentWindow.location.origin &&
                field.contentWindow.location.origin !== this.origin
              ) {
                return;
              }
            } catch (err) {
              // see comment above
              field.contentWindow.postMessage(slaveField, this.origin);
            }
          }
        });
      }
    });
  }

  getMessageStream() {
    return Rx.fromEvent<MessageEvent>(window, 'message').pipe(
      filter(m => m.origin === this.origin && typeof m.data === 'object'),
      map(m => m.data),
    );
  }

  /*
    async reload() {
      this.fields.pan.src = await this.url();
    }
  */

  // Same as the commented out function above without async await
  // as the powers the be have determined that we aren't using them.
  reload() {
    this.url().then(url => (this.fields.pan.src = url));
  }

  submit(amount: number, currencyCode: string, meta: CCFormExtraPayload): void {
    if (this.fields && this.fields.pan && this.fields.pan.contentWindow) {
      this.fields.pan.contentWindow.postMessage(
        { amount, currencyCode, meta },
        this.origin,
      );
    }
  }

  style(styling: Style): void {
    Object.keys(styling).forEach(s => {
      const f = this.fields[s];
      if (f.src && f.contentWindow) {
        f.contentWindow.postMessage(styling, this.origin);
      }
    });
  }
}
